<template>
  <v-card>
    <v-alert class="alert" :type="alertType" v-if="alert">{{
      alertMessage
    }}</v-alert>
    <v-toolbar flat>
      <v-toolbar-title class="headline">Contacts</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn
        :loading="syncingContacts"
        :disabled="syncingContacts"
        class="mr-2"
        color="primary"
        @click.native.stop="syncContacts"
        >Sync Contacts</v-btn
      >
      <v-btn
        class="mr-2"
        color="primary"
        @click.native.stop="showGroupsManager = true"
        >Manage Groups</v-btn
      >
      <v-btn color="primary" @click.native.stop="contactFormDialog = true"
        >New Contact</v-btn
      >
    </v-toolbar>
    <v-container class="py-0" fluid>
      <v-row align-content="space-around" align="center">
        <v-col cols="4" sm="3">
          <v-switch
            v-model="myContactsOnly"
            label="My contacts"
            hide-details
            class="mt-0"
          ></v-switch>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="8" sm="4">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            hide-details
            single-line
          ></v-text-field>
        </v-col>
      </v-row>
    </v-container>
    <v-data-table
      :footer-props="{
        'items-per-page-options': [-1]
      }"
      :headers="headers"
      hide-default-footer
      :items="filteredContacts"
      item-key="saId"
      :loading="$apollo.loading"
      :search="search"
      :sort-by="['name']"
      :sort-desc="[false]"
    >
      <template v-slot:item.saId="{ item }">
        <a
          class="go-to-case-link mr-2"
          target="_blank"
          :href="
            `https://cs-web.airdesksolutions.com/SA/Edit_Contact.aspx?ID=${item.saId}&CTG=${item.saCtg}`
          "
        >
          {{ item.saId }}
        </a>
        <a v-if="item.needsManualUpdate">
          <v-icon color="red" size="15">mdi-alert-outline</v-icon>
        </a>
      </template>
      <template v-slot:item.priority="{ item }">
        <span
          @click="updatePriority(item.id, false)"
          v-if="item.priority && item.priority_level"
          :class="getPriorityTextColor(item.priority_level)"
          >Level {{ item.priority_level }}</span
        >
        <span
          @click="updatePriority(item.id, false)"
          v-if="item.priority && !item.priority_level"
          class="green--text text--darken-2"
          >Yes</span
        >
        <span
          @click="updatePriority(item.id, true)"
          v-if="!item.priority"
          class="grey--text lighten-3"
          >No</span
        >
      </template>
      <template v-slot:item.tier="{ item }">
        <v-chip v-if="item.tier" x-small :color="tierColor(item.tier)" dark>
          Tier {{ item.tier }}
        </v-chip>
        <span v-else class="grey--text">—</span>
      </template>
      <template v-slot:item.assignees="{ item }">
        <span v-if="item.assignees && item.assignees.length">
          {{ formatAssignees(item.assignees) }}
        </span>
        <span v-else class="grey--text">—</span>
      </template>
      <template v-slot:item.lastTouch="{ item }">
        {{ item.lastTouch ? formatShortDate(item.lastTouch) : "Never" }}
      </template>
      <template v-slot:item.nextDue="{ item }">
        <span :class="item.overdue ? 'red--text font-weight-bold' : ''">
          {{ item.nextDue ? formatShortDate(item.nextDue) : "—" }}
        </span>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-icon small @click="editContact(item)">
          mdi-pencil
        </v-icon>
      </template>
    </v-data-table>
    <v-dialog v-model="contactFormDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ contactFormType }} Contact</span>
        </v-card-title>
        <v-container>
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                v-model="contactName"
                :search-input.sync="contactNameSearch"
                :items="contactNameOptions"
                @keyup="getContactsByName"
                label="Name"
                :loading="$apollo.loading"
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-select
                v-model="contactType"
                label="Type"
                :items="contactTypeOptions"
              ></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="6">
              <v-checkbox
                v-model="acceptsLien"
                label="Accepts Lien"
              ></v-checkbox>
            </v-col>
            <v-col cols="12" sm="6">
              <v-checkbox
                v-model="acceptsHealthInsurance"
                label="Accepts Health Insurance"
              ></v-checkbox>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-checkbox
                v-model="priority"
                label="Priority Contact"
              ></v-checkbox>
            </v-col>
          </v-row>
          <v-row v-if="priority">
            <v-col cols="12">
              <v-select
                v-model="priorityLevel"
                label="Priority Level"
                :items="priorityLevelOptions"
                clearable
                placeholder="Select priority level or leave empty"
              ></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-btn
                @click="addContact"
                color="primary"
                :disabled="!contactType"
                >Add Contact</v-btn
              >
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
    <v-dialog v-model="editContactDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">Edit Contact</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editedContact.name"
                  label="Name"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editedContact.email"
                  label="Email"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editedContact.phone"
                  label="Phone"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editedContact.address"
                  label="Address"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editedContact.city"
                  label="City"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editedContact.state"
                  label="State"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="editedContact.zip"
                  label="ZIP"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-checkbox
                  v-model="editedContact.accepts_lien"
                  label="Accepts Lien"
                ></v-checkbox>
              </v-col>
              <v-col cols="12" sm="6">
                <v-checkbox
                  v-model="editedContact.accepts_health_insurance"
                  label="Accepts Health Insurance"
                ></v-checkbox>
              </v-col>
              <v-col cols="12">
                <v-checkbox
                  v-model="editedContact.priority"
                  label="Priority Contact"
                ></v-checkbox>
              </v-col>
              <v-col v-if="editedContact.priority" cols="12">
                <v-select
                  v-model="editedContact.priority_level"
                  label="Priority Level"
                  :items="priorityLevelOptions"
                  clearable
                  placeholder="Select priority level or leave empty"
                ></v-select>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="editedContact.referral_notes"
                  label="Referral Notes"
                  rows="3"
                  outlined
                  placeholder="Enter referral instructions, email addresses, etc."
                ></v-textarea>
              </v-col>
              <template v-if="isAdmin">
                <v-col cols="12">
                  <v-divider></v-divider>
                  <div class="overline mt-2">Relationship Management</div>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="editedContact.tier"
                    label="Tier"
                    :items="tierOptions"
                    clearable
                    placeholder="No tier (no check-in cadence)"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-autocomplete
                    v-model="editedAssigneeIds"
                    label="Assigned To"
                    :items="userOptions"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                  ></v-autocomplete>
                </v-col>
              </template>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeEdit">Cancel</v-btn>
          <v-btn color="blue darken-1" text @click="saveEdit">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <referral-groups-manager
      v-model="showGroupsManager"
      :contact-options="groupContactOptions"
    />
  </v-card>
</template>

<script>
import gql from "graphql-tag";
import { mapGetters } from "vuex";
import debounce from "lodash/debounce";
import findIndex from "lodash/findIndex";
import sortBy from "lodash/sortBy";
import format from "date-fns/format";
import ReferralGroupsManager from "./ReferralGroupsManager";

export default {
  components: {
    ReferralGroupsManager
  },
  data() {
    return {
      alert: false,
      alertType: false,
      alertMessage: false,
      headers: [
        { text: "SA Link", value: "saId", width: "100px" },
        { text: "Name", value: "name" },
        { text: "Tier", value: "tier", width: "80px" },
        { text: "Assigned", value: "assignees" },
        { text: "Last Touch", value: "lastTouch", width: "110px" },
        { text: "Next Due", value: "nextDue", width: "110px" },
        { text: "Priority", value: "priority" },
        { text: "Inbound", value: "inboundReferrals" },
        { text: "Outbound", value: "outboundReferrals" },
        { text: "Type", value: "contactType.name" },
        { text: "City", value: "city" },
        { text: "State", value: "state" },
        { text: "Actions", value: "actions", sortable: false }
      ],
      contacts: [],
      search: "",
      myContactsOnly: false,
      editedAssigneeIds: [],
      originalAssigneeIds: [],
      contactFormDialog: false,
      contactFormType: "New",
      contactType: "",
      contactName: "",
      contactNameSearch: "",
      contactNameSearchResults: [],
      acceptsLien: false,
      acceptsHealthInsurance: false,
      priority: false,
      priorityLevel: null,
      syncingContacts: false,
      showGroupsManager: false,
      editContactDialog: false,
      editedContact: {
        id: null,
        name: "",
        email: "",
        phone: "",
        address: "",
        city: "",
        state: "",
        zip: "",
        accepts_lien: false,
        accepts_health_insurance: false,
        priority: false,
        priority_level: null,
        referral_notes: ""
      }
    };
  },
  apollo: {
    contacts: {
      query: gql`
        query CRMContacts {
          contacts {
            id
            saId
            saCtg
            contactType {
              name
            }
            name
            address
            city
            state
            zip
            phone
            email
            priority
            inboundReferrals
            outboundReferrals
            lat
            lng
            needsManualUpdate
            accepts_lien
            accepts_health_insurance
            priority_level
            referral_notes
            tier
            assignees {
              id
              firstName
              lastName
            }
            lastTouch
            nextDue
            overdue
          }
        }
      `,
      skip() {
        return this.currentUser ? false : true;
      }
    },
    contactTypes: {
      query: gql`
        query ContactTypes {
          contactTypes {
            id
            name
          }
        }
      `
    },
    organization: {
      query: gql`
        query CRMOrgUsers($id: ID!) {
          organization(id: $id) {
            id
            users {
              id
              firstName
              lastName
              active
            }
          }
        }
      `,
      variables() {
        return { id: this.currentUser.organization.id };
      },
      skip() {
        return !this.currentUser || !this.isAdmin;
      }
    }
  },
  computed: {
    ...mapGetters(["currentUser", "isAdmin"]),
    filteredContacts() {
      if (!this.contacts) return [];
      if (!this.myContactsOnly || !this.currentUser) return this.contacts;
      return this.contacts.filter(
        c =>
          c.assignees &&
          c.assignees.some(
            a => parseInt(a.id, 10) === parseInt(this.currentUser.id, 10)
          )
      );
    },
    tierOptions() {
      return [
        { text: "Tier 1", value: 1 },
        { text: "Tier 2", value: 2 },
        { text: "Tier 3", value: 3 }
      ];
    },
    userOptions() {
      if (!this.organization || !this.organization.users) return [];
      return sortBy(
        this.organization.users.filter(u => u.active),
        ["firstName", "lastName"]
      ).map(u => ({
        text: `${u.firstName} ${u.lastName}`,
        value: parseInt(u.id, 10)
      }));
    },
    groupContactOptions() {
      return (this.contacts || [])
        .filter(c => c && c.saId != null && c.saCtg != null)
        .map(c => ({
          saId: c.saId,
          saCtg: c.saCtg,
          name: c.name
        }));
    },
    contactTypeOptions() {
      if (!this.contactTypes) return [];
      return this.contactTypes.map(c => {
        return {
          text: c.name,
          value: c.id
        };
      });
    },
    contactNameOptions() {
      return this.contactNameSearchResults.map(c => {
        return {
          text: `${c.name} - ${c.address} ${c.city} ${c.state} ${c.zip}`,
          value: c.saId
        };
      });
    },
    priorityLevelOptions() {
      return [
        { text: "None (no priority level)", value: null },
        { text: "1 - Highest Priority (Green)", value: 1 },
        { text: "2 - High Priority (Blue)", value: 2 },
        { text: "3 - Medium Priority (Yellow)", value: 3 },
        { text: "4 - Low Priority (Red)", value: 4 },
        { text: "5 - Do Not Use (Black)", value: 5 }
      ];
    }
  },
  methods: {
    tierColor(tier) {
      const colors = { 1: "green", 2: "blue", 3: "grey" };
      return colors[tier] || "grey";
    },
    formatAssignees(assignees) {
      return assignees.map(a => `${a.firstName} ${a.lastName}`).join(", ");
    },
    formatShortDate(date) {
      // Date-only strings (YYYY-MM-DD) parse as UTC midnight; force local time
      let d = /^\d{4}-\d{2}-\d{2}$/.test(date)
        ? new Date(`${date}T00:00:00`)
        : new Date(date);
      return format(d, "MMM dd, yyyy");
    },
    getPriorityTextColor(priorityLevel) {
      const colorMap = {
        1: "green--text text--darken-2",
        2: "blue--text text--darken-2",
        3: "amber--text text--darken-2",
        4: "red--text text--darken-2",
        5: "black--text"
      };
      const normalizedPriority = priorityLevel
        ? parseInt(priorityLevel, 10)
        : null;
      return colorMap[normalizedPriority] || "grey--text";
    },
    getContactsByName: debounce(function() {
      if (this.contactNameSearch.length < 5) return;
      this.$apollo
        .query({
          query: gql`
            query CRMContactsByName($name: String!) {
              contactsByName(name: $name) {
                saId
                saCtg
                name
                address
                city
                state
                zip
                phone
                email
              }
            }
          `,
          variables: {
            name: this.contactNameSearch
          }
        })
        .then(response => {
          this.contactNameSearchResults = response.data.contactsByName;
        });
    }, 500),
    updatePriority(contactId, priority) {
      this.$apollo
        .mutate({
          mutation: gql`
            mutation updateContact($contactId: Int!, $contact: ContactInput!) {
              updateContact(contactId: $contactId, contact: $contact) {
                id
                name
              }
            }
          `,
          variables: {
            contactId: parseInt(contactId),
            contact: {
              priority
            }
          },
          update: () => {
            this.$apollo.queries.contacts.refetch();
          }
        })
        .then(() => {})
        .catch(error => {
          console.error(error);
          this.alert = true;
          this.alertType = "error";
          this.alertMessage = error.message;
          setTimeout(() => {
            this.alert = false;
          }, 3000);
        });
      return false;
    },
    addContact() {
      let index = findIndex(this.contactNameSearchResults, c => {
        return c.saId === this.contactName;
      });

      let contact = {
        sa_id: this.contactNameSearchResults[index].saId,
        sa_ctg: this.contactNameSearchResults[index].saCtg,
        contact_type_id: parseInt(this.contactType, 10),
        accepts_lien: this.acceptsLien,
        accepts_health_insurance: this.acceptsHealthInsurance,
        priority: this.priority,
        priority_level: this.priorityLevel
      };

      console.log("Creating contact with data:", contact);

      this.$apollo
        .mutate({
          mutation: gql`
            mutation CreateContact($contact: ContactInput!) {
              createContact(contact: $contact) {
                id
                name
                priority
                priority_level
                accepts_lien
                accepts_health_insurance
              }
            }
          `,
          variables: {
            contact
          },
          update: () => {
            this.$apollo.queries.contacts.refetch();
          }
        })
        .then(response => {
          console.log("Create contact response:", response.data.createContact);
          this.contactFormDialog = false;
          this.contactName = "";
          this.contactType = "";
          this.acceptsLien = false;
          this.acceptsHealthInsurance = false;
          this.priority = false;
          this.priorityLevel = null;
          this.contactNameSearchResults = [];
          this.contactNameSearch = "";
        })
        .catch(error => {
          console.error(error);
          this.alert = true;
          this.alertType = "error";
          this.alertMessage = error.message;
          setTimeout(() => {
            this.alert = false;
          }, 3000);
        });
      return false;
    },
    syncContacts() {
      this.syncingContacts = true;
      this.$apollo
        .mutate({
          mutation: gql`
            mutation SyncContacts {
              syncContacts {
                result
              }
            }
          `,
          update: () => {
            this.$apollo.queries.contacts.refetch();
          }
        })
        .then(() => {
          this.syncingContacts = false;
          this.alert = true;
          this.alertType = "success";
          this.alertMessage = "Contacts synced successfully";
          setTimeout(() => {
            this.alert = false;
          }, 3000);
        })
        .catch(error => {
          this.syncingContacts = false;
          console.error(error);
          this.alert = true;
          this.alertType = "error";
          this.alertMessage = error.message;
          setTimeout(() => {
            this.alert = false;
          }, 3000);
        });
      return false;
    },
    editContact(item) {
      this.editedContact = { ...item };
      this.editedAssigneeIds = (item.assignees || []).map(a =>
        parseInt(a.id, 10)
      );
      this.originalAssigneeIds = [...this.editedAssigneeIds];
      this.editContactDialog = true;
    },
    closeEdit() {
      this.editContactDialog = false;
      this.$nextTick(() => {
        this.editedContact = {
          id: null,
          name: "",
          email: "",
          phone: "",
          address: "",
          city: "",
          state: "",
          zip: "",
          accepts_lien: false,
          accepts_health_insurance: false,
          priority: false,
          priority_level: null,
          referral_notes: "",
          tier: null
        };
        this.editedAssigneeIds = [];
        this.originalAssigneeIds = [];
      });
    },
    saveRelationshipManagement(contactId) {
      let mutations = [];

      mutations.push(
        this.$apollo.mutate({
          mutation: gql`
            mutation UpdateContactTier($contactId: Int!, $tier: Int) {
              updateContactTier(contactId: $contactId, tier: $tier) {
                id
                tier
              }
            }
          `,
          variables: {
            contactId,
            tier: this.editedContact.tier || null
          }
        })
      );

      let added = this.editedAssigneeIds.filter(
        id => !this.originalAssigneeIds.includes(id)
      );
      let removed = this.originalAssigneeIds.filter(
        id => !this.editedAssigneeIds.includes(id)
      );

      added.forEach(userId => {
        mutations.push(
          this.$apollo.mutate({
            mutation: gql`
              mutation AssignContact($contactId: Int!, $userId: Int!) {
                assignContact(contactId: $contactId, userId: $userId) {
                  id
                }
              }
            `,
            variables: { contactId, userId }
          })
        );
      });

      removed.forEach(userId => {
        mutations.push(
          this.$apollo.mutate({
            mutation: gql`
              mutation UnassignContact($contactId: Int!, $userId: Int!) {
                unassignContact(contactId: $contactId, userId: $userId) {
                  id
                }
              }
            `,
            variables: { contactId, userId }
          })
        );
      });

      return Promise.all(mutations);
    },
    saveEdit() {
      this.$apollo
        .mutate({
          mutation: gql`
            mutation updateContact($contactId: Int!, $contact: ContactInput!) {
              updateContact(contactId: $contactId, contact: $contact) {
                id
                name
                email
                phone
                address
                city
                state
                zip
                accepts_lien
                accepts_health_insurance
                priority
                priority_level
                referral_notes
              }
            }
          `,
          variables: {
            contactId: parseInt(this.editedContact.id, 10),
            contact: {
              name: this.editedContact.name,
              email: this.editedContact.email,
              phone: this.editedContact.phone,
              address: this.editedContact.address,
              city: this.editedContact.city,
              state: this.editedContact.state,
              zip: this.editedContact.zip,
              accepts_lien: this.editedContact.accepts_lien,
              accepts_health_insurance: this.editedContact
                .accepts_health_insurance,
              priority: this.editedContact.priority,
              priority_level: this.editedContact.priority_level,
              referral_notes: this.editedContact.referral_notes
            }
          }
        })
        .then(() => {
          if (this.isAdmin) {
            return this.saveRelationshipManagement(
              parseInt(this.editedContact.id, 10)
            );
          }
        })
        .then(() => {
          this.closeEdit();
          this.$apollo.queries.contacts.refetch();
        })
        .catch(error => {
          console.error(error);
          this.alert = true;
          this.alertType = "error";
          this.alertMessage = error.message;
          setTimeout(() => {
            this.alert = false;
          }, 3000);
        });
    }
  }
};
</script>

<style scoped lang="scss">
.alert {
  position: fixed;
  left: 50%;
  bottom: 50px;
  transform: translate(-50%, -50%);
  margin: 0 auto;
}
</style>
